/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ChipsTextField.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 16-08-2024
 * @description : Reusable text field to add/remove text
 */

import { Box, Chip, TextField, IconButton, Typography } from "@mui/material";
import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { componentStyle } from "../../config/appTheme";

function ChipsTextField({
  size = "small",
  label,
  placeholder,
  variant = "outlined",
  txtFldStyle = {},
  chips,
  setChips,
  required = false,
  disabled,
}) {
  const [inputValue, setInputValue] = useState("");

  const handleAddChip = (event) => {
    console.log("evens,mandjhas", event);
    if (event.key === "Enter" && inputValue.trim() !== "") {
      addChipToList();
    }
  };

  const addChipToList = () => {
    if (inputValue.trim() !== "") {
      // Check if the chip already exists in the list
      if (!chips.includes(inputValue.trim())) {
        console.log("chipschipschips", chips);
        setChips([...chips, inputValue.trim()]);
      }
      setInputValue("");
    }
  };

  const handleAddButtonClick = () => {
    addChipToList();
  };

  const handleDeleteChip = (chipToDelete) => {
    const updated = chips.filter((chip) => chip !== chipToDelete);
    setChips(updated);
  };

  // TextField styling to match CustomFormTextFieldV2
  const textFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#d9d9d9",
        borderRadius: "0px",
        height: "41px",
      }, // Default
      "&:hover fieldset": { borderColor: "#8c8c8c" }, // Hover
      "&.Mui-focused fieldset": { borderColor: "#000000" }, // Focused
      "&.Mui-disabled fieldset": { borderColor: "#e0e0e0" }, // Disabled
      "&.Mui-error fieldset": { borderColor: "#e53935" }, // Error
      "& .MuiInputBase-input": {
        ...componentStyle.font.form.textFieldValue,
        height: "32px", // ✅ ensures input area matches field height
        padding: "4px 8px", // ✅ centers text vertically (no top/bottom padding)
        display: "flex",
        alignItems: "center", // ✅ centers text baseline properly
        boxSizing: "border-box",
        lineHeight: "normal", // ✅ prevents excess vertical spacing
      },

      // 🔹 Change text color on hover
      "&:hover .MuiInputBase-input::placeholder": {
        color: "#848587", // dark text color on hover
      },

      // 🔹 Change text color on focus (selected)
      "&.Mui-focused .MuiInputBase-input::placeholder": {
        color: "#232323", // solid black when focused
      },
    },

    "& .MuiInputLabel-root": {
      color: "#000",
      ...componentStyle.font.form.textFieldValue,
      "&.Mui-error": { color: "#e53935" },
      "&.Mui-disabled": { color: "#bdbdbd" },
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#999",
      opacity: 1,
      ...componentStyle.font.form.textFieldValue,
    },
    "& .MuiFormHelperText-root.Mui-error": {
      color: "#e53935",
      ...componentStyle.font.form.textFieldError,
      marginLeft: 0,
      marginTop: "4px",
    },
    ...txtFldStyle,
  };

  return (
    <Box>
      {/* Label - matching CustomFormTextFieldV2 exactly */}
      {label && (
        <Typography
          sx={{
            mb: 0.5,
            color: "#6f7174",
            ...componentStyle.font.form.textFieldLabel,
            fontWeight: 500,
          }}
        >
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </Typography>
      )}

      {/* Input field with add button */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <TextField
          disabled={disabled}
          size={size}
          placeholder={placeholder}
          variant={variant}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleAddChip}
          fullWidth
          sx={textFieldStyle}
        />
        <IconButton
          onClick={handleAddButtonClick}
          disabled={!inputValue.trim() || disabled}
          sx={{
            backgroundColor: inputValue.trim() ? "#26890D" : "#E0E0E0",
            color: inputValue.trim() ? "white" : "#9E9E9E",
            borderRadius: 0,
            width: 24,
            height: 24,
            "&:hover": {
              backgroundColor: inputValue.trim() ? "#1e6b0a" : "#E0E0E0",
            },
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Chips container */}
      <Box
        sx={{
          ...styles.chipsContainer,
          ...(chips?.length > 0 && {
            border: "1px solid lightgrey",
            mt: 1,
          }),
        }}
      >
        {chips?.map((chip, index) => (
          <Chip
            disabled={disabled}
            key={index}
            label={chip}
            size="small"
            onDelete={() => handleDeleteChip(chip)}
            deleteIcon={<CloseIcon sx={{ color: "white" }} />}
            sx={{
              mb: 0.5,
              mr: 0.5,
              backgroundColor: "#232323",
              color: "white",
              borderRadius: 0,
              "& .MuiChip-deleteIcon": {
                color: "white !important",
              },
              "& .MuiChip-deleteIcon:hover": {
                color: "#f5f5f5 !important",
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

export default ChipsTextField;

// Styling
const styles = {
  chipsContainer: {
    maxHeight: "95px",
    maxWidth: "100%",
    overflow: "auto",
    px: 0.5,
    pt: 0.5,
    mb: 2,
  },
  chip: {
    mb: 0.5,
    backgroundColor: "#232323",
    color: "white",
    borderRadius: 0,
  },
};
